// PM5 device-info + sample-rate helpers. Kept OUT of pm5Connection.js so the
// calibrated erg parsing there is untouched.
//   • readPm5DeviceInfo  — reads CE060012 (serial, 9-byte str) + CE060014
//                          (firmware version, 20-byte str) from the Device
//                          Information service and surfaces them to Settings.
//   • writePm5SampleRate — writes CE060034 (status sample rate) to the fastest
//                          rate the PM5 accepts (3 = 100 ms) and logs it.
// Concept2 base UUID: CE06XXXX-43E5-11E4-916C-0800200C9A66.

const C2_BASE = 'ce06{0}-43e5-11e4-916c-0800200c9a66';
const uuid = (id) => C2_BASE.replace('{0}', id.toString(16).padStart(4, '0'));

const SERIAL_UUID = uuid(0x0012);
const FIRMWARE_UUID = uuid(0x0014);
const SAMPLE_RATE_UUID = uuid(0x0034);

// 0 = 1 s, 1 = 500 ms, 2 = 250 ms, 3 = 100 ms (fastest) — per C2 BLE spec.
export const SAMPLE_RATE_FASTEST = 3;
const SAMPLE_RATE_LABELS = { 0: '1 s', 1: '500 ms', 2: '250 ms', 3: '100 ms' };

// 0x0012/0x0014/0x0034 live in different services, so scan every primary one.
async function findChar(conn, u) {
  const direct = conn?.characteristics?.[u];
  if (direct) return direct;
  const server = conn?.server || conn?.device?.gatt;
  if (!server?.getPrimaryServices) return null;
  try {
    const services = await server.getPrimaryServices();
    for (const svc of services) {
      try {
        const c = await svc.getCharacteristic(u);
        if (c) { if (conn.characteristics) conn.characteristics[u] = c; return c; }
      } catch { /* not in this service */ }
    }
  } catch { /* discovery failed */ }
  return null;
}

function decodeStr(dv) {
  try {
    return new TextDecoder('utf-8').decode(dv).replace(/\0+$/g, '').trim();
  } catch {
    let s = '';
    for (let i = 0; i < dv.byteLength; i += 1) { const b = dv.getUint8(i); if (b) s += String.fromCharCode(b); }
    return s.trim();
  }
}

export async function readPm5DeviceInfo(conn) {
  const info = { serial: null, firmware: null };
  try {
    const sc = await findChar(conn, SERIAL_UUID);
    if (sc?.readValue) info.serial = decodeStr(await sc.readValue());
  } catch (e) { console.warn('PM5: serial (CE060012) read failed', e?.message || e); }
  try {
    const fc = await findChar(conn, FIRMWARE_UUID);
    if (fc?.readValue) info.firmware = decodeStr(await fc.readValue());
  } catch (e) { console.warn('PM5: firmware (CE060014) read failed', e?.message || e); }
  console.log(`PM5 device info: serial=${info.serial || 'n/a'}, firmware=${info.firmware || 'n/a'}`);
  window.__crewzPm5DeviceInfo = info;
  window.dispatchEvent(new CustomEvent('crewz-pm5-deviceinfo', { detail: info }));
  return info;
}

export async function writePm5SampleRate(conn, rate = SAMPLE_RATE_FASTEST) {
  try {
    const c = await findChar(conn, SAMPLE_RATE_UUID);
    if (!c) {
      console.warn('PM5: sample-rate characteristic CE060034 not found');
      window.__crewzPm5SampleRate = { value: null, error: 'CE060034 not found' };
      window.dispatchEvent(new CustomEvent('crewz-pm5-samplerate', { detail: window.__crewzPm5SampleRate }));
      return null;
    }
    const payload = Uint8Array.of(rate & 0xff);
    if (c.writeValueWithResponse) await c.writeValueWithResponse(payload);
    else await c.writeValue(payload);
    const label = SAMPLE_RATE_LABELS[rate] || String(rate);
    console.log(`PM5: wrote sample rate CE060034 = ${rate} (${label}, fastest)`);
    window.__crewzPm5SampleRate = { value: rate, label };
    window.dispatchEvent(new CustomEvent('crewz-pm5-samplerate', { detail: window.__crewzPm5SampleRate }));
    return rate;
  } catch (e) {
    console.warn('PM5: sample-rate (CE060034) write failed', e?.message || e);
    window.__crewzPm5SampleRate = { value: null, error: String(e?.message || e) };
    window.dispatchEvent(new CustomEvent('crewz-pm5-samplerate', { detail: window.__crewzPm5SampleRate }));
    return null;
  }
}
